import { Textarea } from '@/components/ui/FormControls';
import { financialSummary } from '@/lib/salesOverview';
import PermissionGate from '@/components/common/PermissionGate';
// ─────────────────────────────────────────────
// KLOTHIQ — Sale Detail Page (wired)
// ─────────────────────────────────────────────

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import { useSale, useDecideSaleReturn } from '@/hooks/useSales';
import { useCashAccounts } from '@/hooks/useCash';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import SaleWhatsAppAction from '@/components/sales/SaleWhatsAppAction';
import SaleGreetingAction from '@/components/sales/SaleGreetingAction';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import StatusBadge from '@/components/common/StatusBadge';
import PaymentMethodBadge from '@/components/common/PaymentMethodBadge';
import ExportActions from '@/components/common/ExportActions';
import DeleteSaleModal from '@/components/sales/DeleteSaleModal';
import ReceiptPreviewModal from '@/components/common/ReceiptPreviewModal';
import EditSaleMetadataModal from '@/components/sales/EditSaleMetadataModal';
import RequestReturnModal from '@/components/sales/RequestReturnModal';
import CompleteReturnModal from '@/components/sales/CompleteReturnModal';
import EditPaymentModal, { type PaymentItem } from '@/components/sales/EditPaymentModal';
import SaleTrackingTab from '@/components/sales/SaleTrackingTab';
import SaleStatusMultiSelectPopover from '@/components/sales/SaleStatusMultiSelectPopover';
import { SaleReceiptData } from '@/utils/receiptGenerator';
import { getOperationalStatusTags } from '@/utils/statusUtils';
import {
  Pencil,
  Package,
  IndianRupee,
  Loader2,
  CreditCard,
  Trash2,
  RotateCcw,
  Check,
  X as XIcon,
  AlertTriangle,
} from 'lucide-react';
import { useConfirm } from '@/contexts/ConfirmContext';
import { toast } from 'sonner';

function formatRupee(val: number) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(val);
}

export default function SaleDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: sale, isLoading, error, refetch } = useSale(id);
  const decideReturn = useDecideSaleReturn();
  const { data: accounts } = useCashAccounts();
  const confirm = useConfirm();
  const { role, can } = useAuth();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showRequestReturnModal, setShowRequestReturnModal] = useState(false);
  const [showCompleteReturnModal, setShowCompleteReturnModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<PaymentItem | null>(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'tracking'>('overview');

  if (isLoading) return <LoadingState message="Loading sale…" />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!sale) return null;

  const s = sale as typeof sale & {
    customers: {
      id: string;
      name: string;
      phone: string | null;
      address: string | null;
      city: string | null;
      pincode: string | null;
    } | null;
    sale_items: Array<{
      id: string;
      quantity: number;
      unit_price: number;
      unit_cost: number;
      discount: number;
      line_total: number;
      returned_qty: number;
      product_variants: {
        sku: string;
        size: string;
        color: string | null;
        default_price: number;
        products: { name: string; product_code: string } | null;
      } | null;
    }>;
    payments: Array<{
      id: string;
      amount: number;
      payment_method: string;
      payment_date: string;
      payment_type: string;
      account_id?: string;
      reference_number?: string | null;
      notes?: string | null;
      is_void?: boolean;
      cash_accounts?: { id: string; name: string } | null;
    }>;
    return_requested_items: Array<{ sale_item_id: string; quantity: number }> | null;
  };

  const financial = financialSummary(s);
  const dueLabel = financial.return_review
    ? 'Review return'
    : financial.amount_due === null
      ? 'Not applicable'
      : formatRupee(financial.amount_due);
  const canChangeStatus = can('sales.status');

  const canEdit = !s.is_voided && !s.is_deleted && can('sales.edit');
  const canEditMetadata = !s.is_voided && can('sales.edit');
  const canPay =
    ['booked', 'confirmed', 'delivery_pending', 'delivered', 'partially_returned'].includes(
      s.sale_status,
    ) &&
    (financial.amount_due ?? 0) > 0 &&
    !financial.return_review &&
    can('sales.collect');

  // Return workflow (FEAT-SALE-STATUS-001)
  const canRequestReturn =
    ['delivered', 'partially_returned'].includes(s.sale_status) &&
    !s.is_voided &&
    (s.return_status === null || s.return_status === 'rejected') &&
    can('sales.return');
  const canDecideReturn =
    s.return_status === 'requested' && can('sales.return') && can('sales.refund');
  const canCompleteReturn =
    s.return_status === 'accepted' && can('sales.return') && can('sales.refund');

  const returnableItems = (s.sale_items ?? []).map((item: (typeof s.sale_items)[0]) => ({
    sale_item_id: item.id,
    product_name: item.product_variants?.products?.name ?? 'Product',
    sku: item.product_variants?.sku,
    variant_label: [item.product_variants?.size, item.product_variants?.color]
      .filter(Boolean)
      .join(' / '),
    quantity: item.quantity,
    returned_qty: item.returned_qty,
  }));

  const acceptedReturnItems = (s.return_requested_items ?? []).map(
    (req: { sale_item_id: string; quantity: number }) => {
      const item = (s.sale_items ?? []).find(
        (si: (typeof s.sale_items)[0]) => si.id === req.sale_item_id,
      );
      return {
        sale_item_id: req.sale_item_id,
        product_name: item?.product_variants?.products?.name ?? 'Product',
        sku: item?.product_variants?.sku,
        variant_label: [item?.product_variants?.size, item?.product_variants?.color]
          .filter(Boolean)
          .join(' / '),
        quantity: req.quantity,
      };
    },
  );

  const customerDisplayName =
    ((s as Record<string, unknown>).customer_name as string) ||
    s.customers?.name ||
    'Walk-in Customer';
  const customerDisplayPhone =
    ((s as Record<string, unknown>).customer_mobile as string) || s.customers?.phone;
  const customerDisplayAddress =
    ((s as Record<string, unknown>).customer_address as string) ||
    [s.customers?.address, s.customers?.city, s.customers?.pincode].filter(Boolean).join(', ');

  const handleAcceptReturn = async () => {
    const isOk = await confirm({
      title: 'Accept Return Request',
      description: `Approve this return? Staff will still need to Complete it once the item is physically back before stock/refund is affected.`,
      variant: 'primary' as const,
      confirmText: 'Accept',
    });
    if (!isOk) return;
    try {
      await decideReturn.mutateAsync({ sale_id: id!, decision: 'accepted' });
      toast.success('Return accepted');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept return');
    }
  };

  const handleRejectReturn = async () => {
    if (!rejectNote.trim()) {
      toast.error('A reason is required to reject a return');
      return;
    }
    try {
      await decideReturn.mutateAsync({
        sale_id: id!,
        decision: 'rejected',
        note: rejectNote.trim(),
      });
      toast.success('Return rejected');
      setShowRejectForm(false);
      setRejectNote('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject return');
    }
  };

  const receiptData: SaleReceiptData = {
    sale_number: s.sale_number,
    order_number: s.order_number,
    sale_date: s.sale_date,
    sale_status: s.sale_status,
    payment_status: s.payment_status,
    customer:
      s.customers || (s as Record<string, unknown>).customer_name
        ? {
            name: customerDisplayName,
            phone: customerDisplayPhone ?? null,
            address: customerDisplayAddress || null,
            city: s.customers?.city ?? null,
            pincode: s.customers?.pincode ?? null,
          }
        : null,
    items: (s.sale_items ?? []).map((item: any) => ({
      id: item.id,
      name: item.product_variants?.products?.name ?? 'Product',
      sku: item.product_variants?.sku,
      size: item.product_variants?.size,
      color: item.product_variants?.color,
      quantity: item.quantity,
      unit_price: Number(item.unit_price),
      discount: Number(item.discount),
      line_total: Number(item.line_total),
    })),
    subtotal: (s.sale_items ?? []).reduce(
      (sum: number, item: (typeof s.sale_items)[0]) =>
        sum + Number(item.quantity) * Number(item.unit_price),
      0,
    ),
    delivery_charge: Number((s as any).delivery_charge ?? 0),
    discount_total: (s.sale_items ?? []).reduce(
      (sum: number, item: (typeof s.sale_items)[0]) => sum + Number(item.discount),
      0,
    ),
    total_amount: Number(s.total_amount),
    paid_amount: financial.net_collected,
    balance_due: financial.amount_due,
    balance_note: financial.return_review
      ? 'Return adjustment requires balance review'
      : financial.amount_due === null
        ? 'Not applicable to draft or cancelled orders'
        : undefined,
    notes: s.notes,
  };

  return (
    <div>
      {financial.return_review && (
        <p className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          Return recorded. The invoice total is unchanged; confirm the return adjustment before
          collecting any further balance. Existing payments remain editable.
        </p>
      )}
      <PageHeader
        title={`Sale ${s.sale_number}`}
        description={customerDisplayName}
        backTo="/sales"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {['courier', 'local_delivery', 'unspecified'].includes(
              (s as { fulfillment_method?: string }).fulfillment_method || '',
            ) &&
              !s.is_voided &&
              !s.is_deleted && (
                <Link
                  to={`/deliveries/${s.id}`}
                  className="inline-flex min-h-11 items-center rounded-xl border border-border px-3 text-sm text-primary"
                >
                  View delivery
                </Link>
              )}
            {!s.is_voided && !s.is_deleted && <SaleWhatsAppAction saleId={s.id} label />}
            {!s.is_voided && !s.is_deleted && (
              <SaleGreetingAction
                sale={{ id: s.id, number: s.sale_number, customerName: customerDisplayName }}
              />
            )}
            <ExportActions
              exportType="saleReceipt"
              sale={{
                ...s,
                sale_number: s.sale_number,
                sale_date: s.sale_date,
                customer_name: customerDisplayName,
                seller_name: ((s as Record<string, unknown>).seller_name as string) ?? '—',
                subtotal: Number((s as Record<string, unknown>).subtotal_amount ?? s.total_amount),
                discount: Number((s as Record<string, unknown>).discount_amount ?? 0),
                total: Number(s.total_amount),
                amount_paid: financial.net_collected,
                status: s.sale_status,
                customer: s.customers
                  ? {
                      name: s.customers.name,
                      phone: s.customers.phone,
                      address: s.customers.address,
                      city: s.customers.city,
                      pincode: s.customers.pincode,
                    }
                  : null,
                sale_items: (s.sale_items ?? []).map((item: (typeof s.sale_items)[0]) => ({
                  product_name: item.product_variants?.products?.name ?? 'Product',
                  sku: item.product_variants?.sku,
                  size: item.product_variants?.size,
                  color: item.product_variants?.color,
                  quantity: item.quantity,
                  unit_price: Number(item.unit_price),
                  total_price: Number(item.line_total),
                })),
              }}
              onViewReceipt={() => setShowReceiptModal(true)}
              excelOptions={() => ({
                fileName: `Invoice_${s.sale_number}`,
                sheetName: 'Invoice',
                columns: [
                  { header: 'Product Name', key: 'product' },
                  { header: 'Variant', key: 'variant' },
                  { header: 'Qty', key: 'qty', format: 'integer' },
                  { header: 'Unit Price', key: 'price', format: 'currency', width: 16 },
                  { header: 'Discount', key: 'discount', format: 'currency', width: 16 },
                  { header: 'Line Total', key: 'total', format: 'currency', width: 16 },
                ],
                data: (s.sale_items ?? []).map((item: (typeof s.sale_items)[0]) => ({
                  product: item.product_variants?.products?.name ?? '—',
                  variant:
                    `${item.product_variants?.size ?? ''} ${item.product_variants?.color ? '/' + item.product_variants.color : ''}`.trim(),
                  qty: item.quantity,
                  price: Number(item.unit_price),
                  discount: Number(item.discount),
                  total: Number(item.line_total),
                })),
              })}
              pdfOptions={() => ({
                fileName: `Invoice_${s.sale_number}`,
                title: `INVOICE — ${s.sale_number}`,
                subtitle: `Customer: ${s.customers?.name ?? 'Walk-in'} ${s.customers?.phone ? '(' + s.customers.phone + ')' : ''} · Date: ${s.sale_date}`,
                columns: [
                  { header: 'Product', key: 'product', width: 68 },
                  { header: 'Variant', key: 'variant', width: 32 },
                  { header: 'Qty', key: 'qty', format: 'integer', width: 16 },
                  { header: 'Price (Rs.)', key: 'price', format: 'currency', width: 22 },
                  { header: 'Discount (Rs.)', key: 'discount', format: 'currency', width: 22 },
                  { header: 'Total (Rs.)', key: 'total', format: 'currency', width: 22 },
                ],
                rows: (s.sale_items ?? []).map((item: (typeof s.sale_items)[0]) => ({
                  product: item.product_variants?.products?.name ?? '—',
                  variant:
                    `${item.product_variants?.size ?? ''} ${item.product_variants?.color ? '/' + item.product_variants.color : ''}`.trim(),
                  qty: item.quantity,
                  price: Number(item.unit_price),
                  discount: Number(item.discount),
                  total: Number(item.line_total),
                })),
                summaryLines: [
                  { label: 'Total Amount', value: `Rs. ${Number(s.total_amount).toFixed(2)}` },
                  { label: 'Net collected', value: `Rs. ${financial.net_collected.toFixed(2)}` },
                  { label: 'Balance Due', value: dueLabel },
                ],
              })}
              onPrint={() => setShowReceiptModal(true)}
            />
            {canEdit && (
              <Link
                to={`/sales/${id}/edit`}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent"
              >
                <Pencil className="h-4 w-4" /> Edit
              </Link>
            )}
            <PermissionGate permission="sales.delete">
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/20"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </PermissionGate>
          </div>
        }
      />

      {/* Actionable Composite Operational Status Alerts */}
      {(() => {
        const operationalTags = getOperationalStatusTags(s);
        const isDeliveredUnpaid =
          operationalTags.includes('delivered') && operationalTags.includes('payment_pending');
        const isReturnRefundPending = operationalTags.includes('refund_pending');

        return (
          <>
            {isDeliveredUnpaid && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3.5 text-sm text-amber-900 dark:text-amber-200">
                <div className="flex items-center gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <div>
                    <strong className="font-semibold">Delivered · Payment Pending:</strong> This
                    order is marked Delivered, but has an unpaid balance of{' '}
                    <span className="font-bold text-amber-700 dark:text-amber-300">{dueLabel}</span>
                    .
                  </div>
                </div>
                {canPay && (
                  <button
                    type="button"
                    onClick={() => navigate(`/sales/${s.id}/edit#payments`)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-amber-700 transition-colors shrink-0"
                  >
                    <IndianRupee className="h-3 w-3" /> Collect Payment
                  </button>
                )}
              </div>
            )}

            {isReturnRefundPending && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-purple-500/40 bg-purple-500/10 p-3.5 text-sm text-purple-900 dark:text-purple-200">
                <div className="flex items-center gap-2.5">
                  <RotateCcw className="h-4 w-4 text-purple-600 dark:text-purple-400 shrink-0" />
                  <div>
                    <strong className="font-semibold">Return Completed · Refund Pending:</strong>{' '}
                    Items have been received into inventory. Customer refund is awaiting processing.
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total', value: formatRupee(Number(s.total_amount)), highlight: true },
          { label: 'Net collected', value: formatRupee(financial.net_collected), highlight: false },
          {
            label: 'Balance Due',
            value: dueLabel,
            highlight: Number(s.balance_due) > 0,
          },
          { label: 'Gross Profit', value: formatRupee(Number(s.gross_profit)), highlight: false },
        ]
          .filter((metric) => metric.label !== 'Gross Profit' || can('financials.view'))
          .map(({ label, value, highlight }) => (
            <div
              key={label}
              className={`rounded-lg border p-3 ${highlight ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}
            >
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {label}
              </p>
              <p
                className={`mt-1 text-base font-bold sm:text-lg ${highlight ? 'text-primary' : 'text-foreground'}`}
              >
                {value}
              </p>
            </div>
          ))}
      </div>

      {/* Status + action strip */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-lg border border-border bg-card px-4 py-3">
        <StatusBadge status={s.sale_status} />
        {s.return_status && <StatusBadge status={`return_${s.return_status}`} />}
        {s.sale_status === 'delivered' && Number(s.balance_due) > 0 && (
          <span className="inline-flex items-center rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400 border border-amber-500/25">
            COD Pending
          </span>
        )}

        {/* Multi-Select Status Dropdown */}
        {canChangeStatus &&
          !s.is_voided &&
          !['courier', 'local_delivery'].includes(
            (s as { fulfillment_method?: string }).fulfillment_method || '',
          ) && (
            <SaleStatusMultiSelectPopover
              sale={s}
              canChangeStatus={canChangeStatus}
              onStatusUpdated={() => refetch()}
            />
          )}

        {/* Return workflow actions */}
        {canRequestReturn && (
          <button
            onClick={() => setShowRequestReturnModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Request Return
          </button>
        )}
        {canDecideReturn && !showRejectForm && (
          <>
            <button
              onClick={handleAcceptReturn}
              disabled={decideReturn.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-60 dark:text-emerald-400"
            >
              {decideReturn.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}{' '}
              Accept Return
            </button>
            <button
              onClick={() => setShowRejectForm(true)}
              disabled={decideReturn.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60"
            >
              <XIcon className="h-3.5 w-3.5" /> Reject
            </button>
          </>
        )}
        {canCompleteReturn && (
          <button
            onClick={() => setShowCompleteReturnModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Package className="h-3.5 w-3.5" /> Complete Return
          </button>
        )}

        {/* Quick Collect Payment trigger from status strip */}
        {canPay && (
          <button
            type="button"
            onClick={() => navigate(`/sales/${s.id}/edit#payments`)}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
          >
            <CreditCard className="h-3.5 w-3.5" /> Collect Payment
          </button>
        )}

        <span className="ml-auto text-xs text-muted-foreground">{s.sale_date}</span>
      </div>

      {/* Return details / reject reason input */}
      {(s.return_status || canDecideReturn) && (
        <div className="mb-4 rounded-lg border border-border bg-card p-4 text-sm shadow-sm space-y-2">
          {s.return_reason && (
            <p>
              <span className="font-semibold text-foreground">Return reason:</span>{' '}
              <span className="text-muted-foreground">{s.return_reason}</span>
            </p>
          )}
          {s.return_status === 'rejected' && s.return_decision_note && (
            <p>
              <span className="font-semibold text-destructive">Rejected:</span>{' '}
              <span className="text-muted-foreground">{s.return_decision_note}</span>
            </p>
          )}
          {s.return_status === 'requested' && !['owner', 'admin'].includes(role ?? '') && (
            <p className="text-xs text-muted-foreground">
              Return requested — awaiting owner/admin approval.
            </p>
          )}
          {showRejectForm && (
            <div className="space-y-2 border-t border-border pt-2">
              <label className="text-xs font-semibold text-foreground">Rejection reason *</label>
              <Textarea
                rows={2}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Why is this return being rejected?"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                helpLabel="Rejection reason *"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectNote('');
                  }}
                  className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectReturn}
                  disabled={decideReturn.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-white hover:bg-destructive/90 disabled:opacity-60"
                >
                  {decideReturn.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{' '}
                  Confirm Reject
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-4 border-b border-border">
        <nav className="flex gap-2" aria-label="Sale Detail Tabs">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'tracking', label: 'Tracking' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'tracking' && (
        <SaleTrackingTab saleId={s.id} saleStatus={s.sale_status} returnStatus={s.return_status} />
      )}

      {activeTab === 'overview' && (
        <>
          {/* Customer card */}
          {(customerDisplayName !== 'Walk-in Customer' || canEditMetadata) && (
            <div className="mb-4 rounded-lg border border-border bg-card p-4 text-sm shadow-sm">
              <div className="flex items-center justify-between pb-2 mb-2 border-b border-border/50">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Customer Details
                </span>
                {canEditMetadata && (
                  <button
                    type="button"
                    onClick={() => setShowMetadataModal(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline focus-visible:outline-none"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit Details
                  </button>
                )}
              </div>
              <p className="font-semibold text-foreground">{customerDisplayName}</p>
              {customerDisplayPhone && (
                <p className="text-muted-foreground">{customerDisplayPhone}</p>
              )}
              {customerDisplayAddress && (
                <p className="mt-1 text-xs text-muted-foreground">{customerDisplayAddress}</p>
              )}
              {s.notes && (
                <p className="mt-2 text-xs italic text-muted-foreground border-t border-border/40 pt-1.5">
                  <span className="font-semibold not-italic">Notes:</span> {s.notes}
                </p>
              )}
            </div>
          )}

          {/* Items */}
          <div className="mb-4 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <div className="border-b border-border px-4 py-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Package className="h-4 w-4 text-muted-foreground" /> Items
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Product / SKU
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                      Qty
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                      Price (₹)
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                      Discount (₹)
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                      Total (₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {s.sale_items?.map((item: (typeof s.sale_items)[0]) => (
                    <tr key={item.id} className="hover:bg-muted/20">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-foreground">
                          {item.product_variants?.products?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.product_variants?.sku} · {item.product_variants?.size}
                          {item.product_variants?.color ? ` / ${item.product_variants.color}` : ''}
                        </p>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {item.quantity}
                        {item.returned_qty > 0 && (
                          <span className="ml-1 text-xs text-destructive">
                            (-{item.returned_qty})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        ₹{Number(item.unit_price).toFixed(2)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">
                        {Number(item.discount) > 0 ? `₹${Number(item.discount).toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold">
                        ₹{Number(item.line_total).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment history */}
          {s.payments && s.payments.length > 0 && (
            <div className="mb-4 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <h3 className="text-sm font-semibold text-foreground">Payment History</h3>
                <span className="text-xs text-muted-foreground">
                  {s.payments.filter((p: any) => !p.is_void).length} payment
                  {s.payments.filter((p: any) => !p.is_void).length === 1 ? '' : 's'} recorded
                </span>
              </div>
              <div className="divide-y divide-border">
                {s.payments.map((pay: (typeof s.payments)[0]) => (
                  <div
                    key={pay.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 hover:bg-muted/20 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PaymentMethodBadge method={pay.payment_method} size="sm" />
                        {pay.cash_accounts?.name && (
                          <span className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground">
                            {pay.cash_accounts.name}
                          </span>
                        )}
                        {pay.reference_number && (
                          <span className="text-xs text-muted-foreground font-mono">
                            Ref: {pay.reference_number}
                          </span>
                        )}
                        {pay.is_void && (
                          <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] font-medium text-destructive">
                            Voided
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                        <span>{pay.payment_date}</span>
                        {pay.notes && <span>· {pay.notes}</span>}
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                      <span
                        className={`text-sm font-semibold ${pay.is_void ? 'text-muted-foreground line-through' : 'text-emerald-600'}`}
                      >
                        {formatRupee(Number(pay.amount))}
                      </span>
                      {!s.is_voided &&
                        !pay.is_void &&
                        can('sales.correct') &&
                        (pay.payment_type !== 'refund' || can('sales.refund')) && (
                          <button
                            type="button"
                            onClick={() => setEditingPayment(pay)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-colors shadow-xs"
                            title="Edit payment details"
                            aria-label={`Edit payment of ${formatRupee(Number(pay.amount))}`}
                          >
                            <Pencil className="h-3 w-3 text-muted-foreground" />
                            <span>Edit</span>
                          </button>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canEdit && (
            <div className="rounded-lg border bg-card p-4">
              <h3 className="text-sm font-semibold">Manage payments</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Correct recorded amounts or add a separate collection from the sale editor.
              </p>
              <Link
                to={`/sales/${s.id}/edit#payments`}
                className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
              >
                <CreditCard className="h-4 w-4" />
                Open payments
              </Link>
            </div>
          )}
        </>
      )}

      {/* Delete Sale Modal */}
      {showDeleteModal && (
        <DeleteSaleModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          saleId={s.id}
          saleNumber={s.sale_number}
          totalAmount={Number(s.total_amount)}
          onSuccess={() => navigate('/sales')}
        />
      )}

      {/* Receipt Preview Modal */}
      {showReceiptModal && (
        <ReceiptPreviewModal
          isOpen={showReceiptModal}
          onClose={() => setShowReceiptModal(false)}
          saleData={receiptData}
        />
      )}

      {/* Edit Customer Metadata Modal */}
      {showMetadataModal && (
        <EditSaleMetadataModal
          isOpen={showMetadataModal}
          onClose={() => setShowMetadataModal(false)}
          saleId={s.id}
          saleNumber={s.sale_number}
          expectedUpdatedAt={s.updated_at}
          initialCustomerId={s.customer_id}
          initialCustomerName={
            ((s as Record<string, unknown>).customer_name as string) || s.customers?.name || ''
          }
          initialCustomerMobile={
            ((s as Record<string, unknown>).customer_mobile as string) || s.customers?.phone || ''
          }
          initialCustomerAddress={
            ((s as Record<string, unknown>).customer_address as string) ||
            [s.customers?.address, s.customers?.city, s.customers?.pincode]
              .filter(Boolean)
              .join(', ') ||
            ''
          }
          initialNotes={s.notes || ''}
          onSuccess={() => refetch()}
        />
      )}

      {/* Request Return Modal */}
      {showRequestReturnModal && (
        <RequestReturnModal
          isOpen={showRequestReturnModal}
          onClose={() => setShowRequestReturnModal(false)}
          saleId={s.id}
          saleNumber={s.sale_number}
          items={returnableItems}
          onSuccess={() => refetch()}
        />
      )}

      {/* Complete Return Modal */}
      {showCompleteReturnModal && (
        <CompleteReturnModal
          isOpen={showCompleteReturnModal}
          onClose={() => setShowCompleteReturnModal(false)}
          saleId={s.id}
          saleNumber={s.sale_number}
          items={acceptedReturnItems}
          onSuccess={() => refetch()}
        />
      )}

      {/* Edit Payment Modal */}
      {editingPayment && (
        <EditPaymentModal
          isOpen={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          payment={editingPayment}
          saleId={s.id}
          saleNumber={s.sale_number}
          accounts={accounts ?? []}
          totalAmount={Number(s.total_amount)}
          paidAmount={Number(s.paid_amount)}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}
